// On-field building meshes. Subscribes to BuildingAdded and spawns a voxel
// box on the matching tile. tileIndex layout mirrors paddy.js's nested loop
// (x outer, z inner) so tileIndex = x * gridSize + z.

import * as THREE from 'three'
import gsap from 'gsap'
import { Field, BuildingConfig, AssetKeys, BuildingTypes, BuildingDioramaTile, BuildingRecipe } from '../config/balance.js'
import { setBurned } from './synty.js'
import { Events, on } from '../ui/eventBus.js'
import * as Assets from '../assets/AssetRegistry.js'
import { createFire } from './effects.js'
import { tileToWorld } from '../game/plotState.js'
import { placeBuildingDiorama, removeBuildingDiorama, rearmDioramaCollision } from './paddy.js'
import { TILE_HEIGHT, HEX_SIZE } from './hexGrid.js'

const AssetKeyByBuilding = {
  [BuildingTypes.GRANARY]: AssetKeys.BUILDING_GRANARY,
  [BuildingTypes.TEMPLE]: AssetKeys.BUILDING_TEMPLE,
  [BuildingTypes.WATCHTOWER]: AssetKeys.BUILDING_WATCHTOWER,
  [BuildingTypes.HOSPITAL]: AssetKeys.BUILDING_HOSPITAL,
  [BuildingTypes.SCHOOL]: AssetKeys.BUILDING_SCHOOL,
  [BuildingTypes.SHOP]: AssetKeys.BUILDING_SHOP,
}

// First-pass mapping of building types → named objects in the low-poly pack
// (Exteriors.glb). Generic names, to be refined once we eyeball them in the
// asset gallery. Pulled via Assets.getByName() + fit() to a hex footprint.
const PackObjectByBuilding = {
  [BuildingTypes.GRANARY]: 'Building_1',
  [BuildingTypes.TEMPLE]: 'Building_3',
  [BuildingTypes.WATCHTOWER]: 'Building_6',
  [BuildingTypes.HOSPITAL]: 'Building_4',
  [BuildingTypes.SCHOOL]: 'Building_5',
  [BuildingTypes.SHOP]: 'Shop_1',
}

// Building footprint in world units — sized to sit within a single hex tile
// (flat-to-flat width ≈ √3 · HEX_SIZE ≈ 1.42). ~0.95 keeps a small margin.
const BUILDING_FOOTPRINT = 0.95

const meshesByTile = new Map() // tileIndex → THREE.Object3D
let sceneRef = null

// Placed building objects (for hover-label raycasting). Each is tagged with
// userData.buildingType.
export function getBuildingObjects() {
  return [...meshesByTile.values()]
}

const firesByTile = new Map() // tileIndex → fire handle from createFire()

export function createBuildings({ scene }) {
  sceneRef = scene
  on(Events.BuildingAdded, ({ type, tileIndex, rotation }) => spawn(type, tileIndex, rotation ?? 0))
  on(Events.BuildingRotated, ({ tileIndex, rotation }) => rotateTo(tileIndex, rotation))
  on(Events.BuildingRemoved, ({ tileIndex }) => removeAt(tileIndex))
  on(Events.KingdomChange, () => { clearAll(); clearAllFires() })
  // Raid arson — pin a flicker-orange flame mesh above the damaged
  // building. Cleared by RepairComplete (which fires PlotChanged per tile).
  on(Events.BuildingDamaged, ({ tileIndex }) => attachFire(tileIndex))
  on(Events.PlotChanged, ({ tileIndex }) => detachFire(tileIndex))
}

function attachFire(tileIndex) {
  if (firesByTile.has(tileIndex)) return
  const building = meshesByTile.get(tileIndex)
  if (!building) return
  // Seat the fire near the building's roofline. Building meshes are
  // centered at height/2 + tileHeight/2, so the top is roughly
  // position.y + height/2; nudge the flames a touch above that.
  let top
  if (building.userData?.diorama) {
    // Diorama wrap — seat the flame near the tallest point of the mini-scene.
    const box = new THREE.Box3().setFromObject(building)
    const c = box.getCenter(new THREE.Vector3())
    top = new THREE.Vector3(c.x, box.max.y + 0.05, c.z)
  } else {
    const cfg = BuildingConfig[building.userData?.buildingType]
    top = building.position.clone()
    top.y += (cfg?.height ?? 0.6) / 2 + 0.05
  }
  const fire = createFire({ scene: sceneRef, position: top, scale: 1.6 })
  firesByTile.set(tileIndex, fire)
  setBurned(building, true) // Synty buildings darken with an ember tint while burning
}

function detachFire(tileIndex) {
  const fire = firesByTile.get(tileIndex)
  if (!fire) return
  fire.dispose()
  firesByTile.delete(tileIndex)
  setBurned(meshesByTile.get(tileIndex), false)
}

function clearAllFires() {
  for (const idx of [...firesByTile.keys()]) detachFire(idx)
}

function clearAll() {
  for (const [tileIndex, obj] of meshesByTile) {
    if (obj.userData?.diorama) {
      // Owned by paddy's diorama layer — let it detach + free the collision.
      removeBuildingDiorama(tileIndex)
      continue
    }
    sceneRef.remove(obj)
    // Only dispose programmatic placeholders; GLB-derived nodes get their
    // geometry/materials cleaned up by the asset registry's caching.
    if (obj.userData?.placeholder) {
      obj.traverse((c) => {
        if (c.isMesh) {
          c.geometry?.dispose?.()
          c.material?.dispose?.()
        }
      })
    }
  }
  meshesByTile.clear()
}

// One facing = 60° (a hex has 6). Clockwise as seen from above is a negative turn about +y.
const facing = (rotation) => -rotation * (Math.PI / 3)

function rotateTo(tileIndex, rotation) {
  const obj = meshesByTile.get(tileIndex)
  if (!obj) return
  gsap.killTweensOf(obj.rotation)
  gsap.to(obj.rotation, {
    y: facing(rotation), duration: 0.28, ease: 'power2.out',
    onComplete: () => rearmDioramaCollision(), // its footprint turned with it
  })
}

// A cancelled / moved placement: take the building off its tile entirely.
function removeAt(tileIndex) {
  const obj = meshesByTile.get(tileIndex)
  if (!obj) return
  detachFire(tileIndex)
  if (obj.userData?.diorama) removeBuildingDiorama(tileIndex)
  else sceneRef.remove(obj)
  meshesByTile.delete(tileIndex)
  rearmDioramaCollision()
}

function spawn(type, tileIndex, rotation = 0) {
  if (meshesByTile.has(tileIndex)) return // idempotent on reload
  const cfg = BuildingConfig[type]
  if (!cfg) return

  // Preferred path: swap the tile's surface for the building's mapped Tile_N
  // diorama (a full mini-scene that reads as a school / temple / shop / etc).
  // paddy owns the placement + collision sampling; we keep the returned wrap
  // for hover-labels + arson-fire anchoring. Pop-in is handled inside paddy.
  const tileNumber = BuildingDioramaTile[type]
  if (tileNumber != null || BuildingRecipe[type]) {
    const wrap = placeBuildingDiorama(tileIndex, tileNumber, BuildingRecipe[type] ?? null)
    if (wrap) {
      wrap.rotation.y = facing(rotation)
      rearmDioramaCollision()
      wrap.userData.buildingType = type
      wrap.userData.tileIndex = tileIndex
      wrap.userData.diorama = true
      meshesByTile.set(tileIndex, wrap)
      return
    }
  }

  // Fallback: standalone prop / placeholder box (asset or mapping missing).
  const obj = Assets.get(AssetKeyByBuilding[type]) ?? packBuilding(type) ?? buildPlaceholder(cfg)
  // Hex tile world position (tileIndex is now an axial "q,r" key).
  const { x: px, z: pz } = tileToWorld(tileIndex)
  // Placeholder gets a y-centered box; GLB authors should anchor their model
  // at y=0 on the base (so it sits on the tile).
  const yOffset = obj.userData?.placeholder
    ? cfg.height / 2 + TILE_HEIGHT / 2
    : TILE_HEIGHT / 2
  obj.position.set(px, yOffset, pz)
  obj.rotation.y = facing(rotation)
  obj.userData.buildingType = type // so attachFire can read the roofline height
  obj.userData.tileIndex = tileIndex

  sceneRef.add(obj)
  meshesByTile.set(tileIndex, obj)

  // Drop-in pop animation so it's clear a new building just landed.
  obj.scale.set(0.01, 0.01, 0.01)
  gsap.to(obj.scale, {
    x: 1,
    y: 1,
    z: 1,
    duration: 0.4,
    ease: 'back.out(2)',
  })
}

// Pull a building model from the low-poly pack, normalized to a hex footprint.
// Returns null if the pack object isn't available (→ placeholder box).
function packBuilding(type) {
  const name = PackObjectByBuilding[type]
  if (!name) return null
  const raw = Assets.getByName(name)
  if (!raw) return null
  return Assets.fit(raw, { footprint: BUILDING_FOOTPRINT, anchorBase: true })
}

function buildPlaceholder(cfg) {
  const footprint = HEX_SIZE * 1.05 // fits within a hex tile's flat-to-flat width
  const geo = new THREE.BoxGeometry(footprint, cfg.height, footprint)
  const mat = new THREE.MeshLambertMaterial({ color: cfg.color })
  const mesh = new THREE.Mesh(geo, mat)
  mesh.userData.placeholder = true
  return mesh
}
